import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import useNotificationSocket, {
  NotificationPayload,
} from '../hooks/useNotificationSocket';
import NotificationService, {
  type Notification,
} from '../services/notificationService';
import { useAuth } from '../hooks/useAuth';

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearError: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle incoming real-time notifications
  const handleNotification = useCallback(
    (payload: NotificationPayload) => {
      const newNotification: Notification = {
        id: payload.id,
        type: payload.type as Notification['type'],
        title: payload.title,
        message: payload.message,
        isRead: false,
        readAt: null,
        createdAt: payload.createdAt,
        actionUrl: payload.actionUrl ?? null,
        actionText: payload.actionText ?? null,
        priority: payload.priority ?? 'MEDIUM',
        data: payload.data ?? {},
        userId: user?.id || '',
      };

      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);

      // Show browser notification if permitted
      if (
        'Notification' in window &&
        window.Notification.permission === 'granted'
      ) {
        new window.Notification(payload.title, {
          body: payload.message,
          icon: '/logo192.png',
        });
      }
    },
    [user?.id]
  );

  const handleUnreadCountUpdate = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Connect to WebSocket when authenticated
  const { isConnected } = useNotificationSocket({
    onNotification: handleNotification,
    onUnreadCountUpdate: handleUnreadCountUpdate,
    enabled: isAuthenticated,
  });

  // Fetch notifications on mount and when user changes
  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      setError(null);
      const result = await NotificationService.getNotifications({ limit: 50 });
      setNotifications(result.notifications);
      setUnreadCount(result.unreadCount);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load notifications'
      );
      console.error('Error loading notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, refreshNotifications]);

  // Request browser notification permission
  useEffect(() => {
    if (
      isAuthenticated &&
      'Notification' in window &&
      window.Notification.permission === 'default'
    ) {
      window.Notification.requestPermission();
    }
  }, [isAuthenticated]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await NotificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n =>
          n.id === id
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to mark notification as read'
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to mark all notifications as read'
      );
    }
  }, []);

  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        const notification = notifications.find(n => n.id === id);
        await NotificationService.deleteNotification(id);
        if (notification && !notification.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        setNotifications(prev => prev.filter(n => n.id !== id));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to delete notification'
        );
      }
    },
    [notifications]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isConnected,
    isLoading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearError,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
}

export default NotificationContext;
